import {FormField, type FormControl} from 'platform/components';
import {Grid, HStack, Icon, Text, VStack} from 'platform/foundation';

import {type FieldPathByValue, type FieldValues} from 'react-hook-form';

import {type Nullish} from 'shared/web';

import {t} from './catalogueUi';

/** The path types `FormField` computes for text and textarea fields, and for apiDate fields. */
type ImporterPriceListFieldNames<TFieldValues extends FieldValues> = Record<
  'catalogueId' | 'priceListVersion' | 'dataVersion' | 'note',
  FieldPathByValue<TFieldValues, unknown>
> &
  Record<
    'releasedOn' | 'ordersOpenFrom' | 'lastOrderDate',
    FieldPathByValue<TFieldValues, string | Nullish>
  >;

export function ImporterPriceListFields<TFieldValues extends FieldValues>(props: {
  control: FormControl<TFieldValues>;
  names: ImporterPriceListFieldNames<TFieldValues>;
  isDisabled?: boolean;
}) {
  return (
    <VStack spacing={4}>
      <FormField
        control={props.control}
        name={props.names.catalogueId}
        type="text"
        label={t('entity.newVehicle.catalogue.importerPriceList.catalogueId', 'Catalogue ID')}
        helperText={t(
          'entity.newVehicle.catalogue.importerPriceList.catalogueIdHelp',
          "The importer's catalogue identifier, e.g. Ford Catalog ID"
        )}
        isDisabled={props.isDisabled}
      />
      <Grid columns={[1, 2]} spacing={3}>
        <FormField
          control={props.control}
          name={props.names.priceListVersion}
          type="text"
          label={t(
            'entity.newVehicle.catalogue.importerPriceList.priceListVersion',
            'Importer price list version (UPP)'
          )}
          helperText={t(
            'entity.newVehicle.catalogue.importerPriceList.priceListVersionHelp',
            'The version dealers quote when ordering'
          )}
          isDisabled={props.isDisabled}
        />
        <FormField
          control={props.control}
          name={props.names.dataVersion}
          type="text"
          label={t(
            'entity.newVehicle.catalogue.importerPriceList.dataVersion',
            'Importer data version (PDL)'
          )}
          isDisabled={props.isDisabled}
        />
      </Grid>
      <Grid columns={[1, 2]} spacing={3}>
        <FormField
          control={props.control}
          name={props.names.releasedOn}
          type="apiDate"
          label={t('entity.newVehicle.catalogue.importerPriceList.releasedOn', 'Release date')}
          isDisabled={props.isDisabled}
        />
      </Grid>
      <Grid columns={[1, 2]} spacing={3}>
        <FormField
          control={props.control}
          name={props.names.ordersOpenFrom}
          type="apiDate"
          label={t(
            'entity.newVehicle.catalogue.importerPriceList.ordersOpenFrom',
            'Orders open from'
          )}
          isDisabled={props.isDisabled}
        />
        <FormField
          control={props.control}
          name={props.names.lastOrderDate}
          type="apiDate"
          label={t(
            'entity.newVehicle.catalogue.importerPriceList.lastOrderDate',
            'Last order date'
          )}
          isDisabled={props.isDisabled}
        />
      </Grid>
      <FormField
        control={props.control}
        name={props.names.note}
        type="textarea"
        label={t('general.labels.note', 'Note')}
        isDisabled={props.isDisabled}
      />
      <HStack spacing={2} align="center">
        <Icon value="action/info_outline" size={4} color="text.secondary" />
        <Text size="small" color="secondary">
          {t(
            'entity.newVehicle.catalogue.importerPriceList.separateFromPricelistVersion',
            'The Omnetic price list version is separate; a mid-year price adjustment moves it on.'
          )}
        </Text>
      </HStack>
    </VStack>
  );
}
